import { Button, Layout } from 'antd';
import type { FC } from 'react';
import React, { useState } from 'react';

import { EditPictureModal } from 'app/components/commons/EditPictureModal/EditPictureModal';
import type { Picture } from 'app/components/commons/Uploader/Types';
import { Uploader } from 'app/components/commons/Uploader/Uploader';
import {
  useAddHotelPictures,
  useEditPicture,
  useRemoveHotelPicture,
} from 'app/hooks/data/pictures/useSavePicture';

import './GalleryDetailEdit.scss';

const { Content, Footer } = Layout;

export const GalleryDetailEdit: FC<Props> = ({
  pictures,
  goToView,
  kind,
  hotelId,
}) => {
  const [uploadedPictures, setUploadedPictures] = useState<Picture[]>([]);
  const [pictureToEdit, setPictureToEdit] = useState<Picture | null>(null);

  const { mutate: addHotelPictures } = useAddHotelPictures();
  const { mutate: editPicture } = useEditPicture();
  const { mutate: removeHotelPicture } = useRemoveHotelPicture();

  const add = (addedPicture: Picture) => {
    const newPicture = {
      ...addedPicture,
      kind,
    };

    setUploadedPictures((previousUploadedPictures) => [
      ...previousUploadedPictures,
      newPicture,
    ]);
    addHotelPictures({ hotelId, kind, pictures: [newPicture] });
  };

  const handleCancel = () => {
    uploadedPictures.forEach((picture) => {
      removeHotelPicture({ hotelId, pictureId: picture.id });
    });
    goToView();
  };

  const renderFooter = () => (
    <Footer className="gallery-detail__footer">
      <div />
      <div>
        <Button size="large" onClick={handleCancel} className="first-button">
          Cancel
        </Button>
        <Button type="primary" size="large" onClick={goToView}>
          Save
        </Button>
      </div>
    </Footer>
  );

  return (
    <>
      <Content className="gallery-detail__content">
        <div className="edit-container">
          <Uploader
            pictures={pictures}
            add={add}
            edit={(picture) => {
              if (picture) {
                setPictureToEdit(picture);
              }
            }}
          />
        </div>
      </Content>
      {renderFooter()}
      {!!pictureToEdit && (
        <EditPictureModal
          picture={pictureToEdit}
          closeModal={() => setPictureToEdit(null)}
          savePicture={(newPictureValues) => {
            editPicture({
              ...pictureToEdit,
              ...newPictureValues,
            });
            setPictureToEdit(null);
          }}
          deletePicture={() => {
            removeHotelPicture({ hotelId, pictureId: pictureToEdit.id });
            setPictureToEdit(null);
          }}
          kind={kind}
          removable
        />
      )}
    </>
  );
};

type Props = {
  pictures: (Picture & { selected: boolean })[];
  goToView: () => void;
  kind: string;
  hotelId: number;
};
